import { useDisclosure } from '@chakra-ui/react';
import { IUsersInfo, UserStatusEnum } from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { handleApiErrorToast } from '../../../../../api/axios';
import { useGetTeamsById } from '../../../../../api/settingsService';
import {
  MBox,
  MFlex,
  MPageContainer,
  MPageLoader,
} from '../../../../../components/Monetize';
import { useConfirmModal } from '../../../../../services/confirmModal';
import { useDocumentHead } from '../../../../../services/documentHead';
import { sortTeamUsers } from '../../../../../utils/settings';
import { SETTINGS_PANE_HEIGHT } from '../../../SideNavBar/SettingsSideNav';
import { TeamDetailsHeader } from '../components/TeamDetailsHeader';
import { AddMembersInline } from './AddMembersInline';
import { OwnerChange } from './OwnerChangeModal';
import { TeamHistoryPanel } from './TeamHistoryPanel';
import { TeamMembersTable } from './TeamMembersTable';
import { useTeamMembership } from './useTeamMembership';

export const TeamDetails = () => {
  const { setDocTitle } = useDocumentHead();
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const { id = '' } = useParams();

  // The raw team (including deactivated users) drives every PUT payload;
  // deactivated members are hidden from the table only.
  const { data: team, isLoading } = useGetTeamsById(id);
  const { addMembers, removeMember, renameTeam, isPending } =
    useTeamMembership(team);

  const {
    open: isOwnerChangeOpen,
    onOpen: onOpenOwnerChange,
    onClose: onCloseOwnerChange,
  } = useDisclosure();

  const {
    showConfirmModal: showRemoveModal,
    setModalLoading: setRemoveModalLoading,
    closeModal: closeRemoveModal,
  } = useConfirmModal();

  useEffect(() => {
    if (team) {
      setDocTitle('Settings', `Teams - ${team.name}`);
    }
  }, [team, setDocTitle]);

  const handleRemoveMember = ({ id: memberId, name }: IUsersInfo) => {
    showRemoveModal({
      title: `Are you sure you want to remove ${name}?`,
      description:
        'Once removed, this user will no longer have access to this team.',
      yesButton: 'Remove',
      yesBtnProps: { variant: 'delete' },
      noBtnProps: { variant: 'cancel' },
      onYes: async () => {
        try {
          setRemoveModalLoading(true);
          await removeMember(memberId);
          closeRemoveModal();
        } catch (error) {
          handleApiErrorToast(error);
        } finally {
          setRemoveModalLoading(false);
        }
      },
    });
  };

  if (isLoading || !team) {
    return <MPageLoader />;
  }

  const activeMembers = sortTeamUsers(
    team.users.filter(({ status }) => status !== UserStatusEnum.DEACTIVATED),
    team.owner,
  );

  // Ownership can move whenever some active member is not already the owner.
  // A plain count would wrongly block the fix-up case where the owner is
  // deactivated and exactly one active member remains.
  const canChangeOwner = activeMembers.some(
    ({ id: memberId }) => memberId !== team.owner,
  );

  return (
    <MFlex
      alignItems="stretch"
      minH={SETTINGS_PANE_HEIGHT}
      data-testid="team-details-page"
    >
      <MPageContainer alignItems="stretch" flex={1} minW={0} minHeight="auto">
        <TeamDetailsHeader
          team={team}
          isHistoryOpen={isHistoryOpen}
          isMutating={isPending}
          canChangeOwner={canChangeOwner}
          onOpenHistory={() => setIsHistoryOpen(true)}
          onRenameTeam={renameTeam}
          onChangeOwner={onOpenOwnerChange}
        />

        <MBox data-testid="team-members-details">
          {/* No section heading: the input's own placeholder says what it does,
              and the table below is self-evident. */}
          <MBox mb={4}>
            <AddMembersInline
              team={team}
              onAdd={addMembers}
              isDisabled={isPending}
            />
          </MBox>

          <TeamMembersTable
            members={activeMembers}
            ownerId={team.owner}
            isMutating={isPending}
            onRemoveMember={handleRemoveMember}
          />
        </MBox>
      </MPageContainer>

      {isHistoryOpen && (
        <TeamHistoryPanel
          teamId={team.id}
          onClose={() => setIsHistoryOpen(false)}
        />
      )}

      <OwnerChange
        isOpen={isOwnerChangeOpen}
        onClose={onCloseOwnerChange}
        teamOwnerId={team.owner}
        teamId={team.id}
        teamUsers={activeMembers}
      />
    </MFlex>
  );
};
